import React, { useState } from 'react';
import './ScheduleDemoModal.css';
import apiService from '../services/apiService';

const ScheduleDemoModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    teamSize: '',
    preferredDate: '',
    preferredTime: '',
    testingChallenges: '',
    applicationTypes: []
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        applicationTypes: [...formData.applicationTypes, value]
      });
    } else {
      setFormData({
        ...formData,
        applicationTypes: formData.applicationTypes.filter(type => type !== value)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await apiService.scheduleDemo(formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Demo scheduling failed:', error);
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  if (isSubmitted) {
    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal-content success-modal">
          <button className="close-button" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <div className="success-content">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6,12 10,16 18,8"></polyline>
              </svg>
            </div>

            <h2>Demo Request Received</h2>
            <p>
              Thank you for your interest in Qate AI. We've received your preferred date and time
              and our team will reach out to confirm the details.
            </p>

            <div className="next-steps">
              <h4>What Happens Next</h4>
              <ul>
                <li>
                  <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  We'll confirm a date and time that works for both sides
                </li>
                <li>
                  <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  You'll receive a calendar invite once confirmed
                </li>
                <li>
                  <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                  Our team will tailor the demo to your specific use case
                </li>
                <li>
                  <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  You'll have the opportunity to ask questions live
                </li>
              </ul>
            </div>

            <button className="primary-button" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content demo-modal">
        <button className="close-button" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div className="modal-header">
          <h2>Request a Demo</h2>
          <p>See Qate AI in action with a personalized demo tailored to your testing needs</p>
        </div>
        
        <form onSubmit={handleSubmit} className="demo-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Your full name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Work Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="your.email@company.com"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company">Company *</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                placeholder="Your company name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="teamSize">Team Size</label>
              <select
                id="teamSize"
                name="teamSize"
                value={formData.teamSize}
                onChange={handleInputChange}
              >
                <option value="">Select team size</option>
                <option value="1-5">1-5 people</option>
                <option value="6-20">6-20 people</option>
                <option value="21-50">21-50 people</option>
                <option value="51-200">51-200 people</option>
                <option value="200+">200+ people</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="preferredDate">Preferred Date</label>
              <input
                type="date"
                id="preferredDate"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleInputChange}
                min={today}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="preferredTime">Preferred Time</label>
              <select
                id="preferredTime"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleInputChange}
              >
                <option value="">Select time preference</option>
                <option value="morning">Morning (9 AM - 12 PM)</option>
                <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                <option value="evening">Evening (5 PM - 8 PM)</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label>What types of applications do you test? (Select all that apply)</label>
            <div className="checkbox-group">
              {[
                'Web applications',
                'Desktop applications (Windows)',
                'Mobile applications',
                'APIs and backend services',
                'E-commerce platforms',
                'Enterprise software'
              ].map((type) => (
                <label key={type} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={type}
                    checked={formData.applicationTypes.includes(type)}
                    onChange={handleCheckboxChange}
                  />
                  <span className="checkmark"></span>
                  {type}
                </label>
              ))}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="testingChallenges">What are your biggest testing challenges?</label>
            <textarea
              id="testingChallenges"
              name="testingChallenges"
              value={formData.testingChallenges}
              onChange={handleInputChange}
              placeholder="Tell us about your current testing pain points, manual processes, or specific scenarios you'd like to see in the demo..."
              rows="3"
            />
          </div>
          
          <div className="demo-highlights">
            <h4>What You'll See in the Demo</h4>
            <div className="highlights-grid">
              <div className="highlight-item">
                <svg className="highlight-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                <span>AI agents testing your actual workflows</span>
              </div>
              <div className="highlight-item">
                <svg className="highlight-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <span>Create tests through conversation</span>
              </div>
              <div className="highlight-item">
                <svg className="highlight-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                <span>Visual element recognition in action</span>
              </div>
              <div className="highlight-item">
                <svg className="highlight-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>
                <span>Real-time test execution and results</span>
              </div>
            </div>
          </div>
          
          <button 
            type="submit" 
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Submitting Request...
              </>
            ) : (
              'Request a Demo'
            )}
          </button>
          
          <p className="privacy-note">
            Your information is secure. We'll only use it to schedule and customize your demo.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ScheduleDemoModal;